import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { History, Building2, FileSpreadsheet, Images, Eye, Download, Undo2 } from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = {
  ante_mortem: "Missing persons",
  post_mortem: "Unidentified bodies",
  photos_zip: "Photos (ZIP)",
};
const KIND_STYLE = {
  ante_mortem: "bg-[#3B6EA5]/10 text-[#3B6EA5] border-[#3B6EA5]/30",
  post_mortem: "bg-[#4A7C59]/10 text-[#4A7C59] border-[#4A7C59]/30",
  photos_zip: "bg-[#8A6A2E]/10 text-[#8A6A2E] border-[#C99B4B]/40",
};

export default function AdminImportLogsPage() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewOrg, setViewOrg] = useState(null);
  const [orgData, setOrgData] = useState(null);
  const [exporting, setExporting] = useState("");
  const [undoing, setUndoing] = useState("");

  const undoImport = async (l) => {
    if (!window.confirm(`Undo this import? This permanently deletes the ${l.created} record(s) created by "${l.filename || l.organization}".`)) return;
    setUndoing(l.id);
    try {
      const { data } = await api.post(`/admin/import-logs/${l.id}/undo`);
      toast.success(`${data.deleted} record(s) deleted`);
      const [r, s] = await Promise.all([api.get("/admin/import-logs"), api.get("/admin/import-logs/summary")]);
      setLogs(r.data);
      setSummary(s.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Undo failed");
    }
    setUndoing("");
  };

  const SYNTHETIC = new Set(["— (unspecified)", "Multiple organisations"]);

  const openOrg = async (org) => {
    setViewOrg(org);
    setOrgData(null);
    try {
      const { data } = await api.get("/admin/organisations/cases", { params: { org } });
      setOrgData(data);
    } catch {
      toast.error("Could not load cases");
    }
  };

  const exportOrg = async (org) => {
    setExporting(org);
    try {
      const res = await api.get("/admin/organisations/export", { params: { org }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${org.replace(/[^a-z0-9]/gi, "_").slice(0, 40)}-cases.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Export failed");
    }
    setExporting("");
  };

  useEffect(() => {
    Promise.all([
      api.get("/admin/import-logs").then((r) => setLogs(r.data)),
      api.get("/admin/import-logs/summary").then((r) => setSummary(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  return (
    <div className="mx-auto max-w-5xl px-6 py-10" data-testid="admin-import-logs-page">
      <p className="font-body text-xs uppercase tracking-[0.18em] text-[#6B7F82]">Admin · data</p>
      <div className="mt-1 flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-[#2E3A46] text-white"><History size={18} /></span>
        <h1 className="font-heading text-3xl font-semibold text-[#1A2024]">Import history</h1>
      </div>
      <p className="mt-2 max-w-2xl font-body text-sm text-[#5C6873]">
        A record of every bulk import, so you can trace the provenance and completeness of each partnership.
      </p>

      {loading && <div className="mt-8 font-body text-sm text-[#5C6873]" data-testid="logs-loading">Loading…</div>}

      {!loading && (
        <>
          <h2 className="mt-8 font-heading text-lg font-semibold text-[#1A2024]">By organisation</h2>
          {summary.length === 0 ? (
            <div className="mt-3 rounded-lg border border-dashed border-[#E2E8F0] bg-[#F8F9FA] p-8 text-center font-body text-sm text-[#5C6873]" data-testid="summary-empty">
              No imports yet.
            </div>
          ) : (
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="org-summary">
              {summary.map((o) => (
                <div key={o.organization} className="rounded-lg border border-[#E2E8F0] bg-white p-4 shadow-sm" data-testid={`summary-${o.organization}`}>
                  <div className="flex items-center gap-2">
                    <Building2 size={15} className="text-[#2E3A46]" />
                    <h3 className="truncate font-heading text-base font-semibold text-[#1A2024]">{o.organization}</h3>
                  </div>
                  <div className="mt-3 flex items-end gap-4">
                    <div>
                      <div className="font-heading text-2xl font-semibold text-[#4A7C59]">{o.created}</div>
                      <div className="font-body text-xs text-[#5C6873]">cases</div>
                    </div>
                    <div>
                      <div className="font-heading text-2xl font-semibold text-[#2E3A46]">{o.photos}</div>
                      <div className="font-body text-xs text-[#5C6873]">photos</div>
                    </div>
                    <div>
                      <div className="font-heading text-2xl font-semibold text-[#1A2024]">{o.imports}</div>
                      <div className="font-body text-xs text-[#5C6873]">imports</div>
                    </div>
                  </div>
                  {o.last_import && (
                    <div className="mt-2 font-body text-xs text-[#6B7F82]">Last: {new Date(o.last_import).toLocaleDateString()}</div>
                  )}
                  {!SYNTHETIC.has(o.organization) && (
                    <div className="mt-3 flex gap-2 border-t border-[#EEF2F5] pt-3">
                      <Button variant="outline" size="sm" className="border-[#E2E8F0] text-[#2E3A46]" onClick={() => openOrg(o.organization)} data-testid={`org-view-${o.organization}`}>
                        <Eye size={13} className="mr-1" /> View cases
                      </Button>
                      <Button variant="outline" size="sm" className="border-[#E2E8F0] text-[#2E3A46]" disabled={exporting === o.organization} onClick={() => exportOrg(o.organization)} data-testid={`org-export-${o.organization}`}>
                        <Download size={13} className="mr-1" /> {exporting === o.organization ? "…" : "CSV"}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <h2 className="mt-10 font-heading text-lg font-semibold text-[#1A2024]">All imports</h2>
          <div className="mt-3 space-y-2" data-testid="logs-list">
            {logs.length === 0 && (
              <div className="rounded-lg border border-dashed border-[#E2E8F0] bg-[#F8F9FA] p-8 text-center font-body text-sm text-[#5C6873]" data-testid="logs-empty">
                No imports recorded yet.
              </div>
            )}
            {logs.map((l) => (
              <div key={l.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#E2E8F0] bg-white p-4 shadow-sm" data-testid={`log-row-${l.id}`}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className={`rounded-full text-[11px] ${KIND_STYLE[l.kind] || ""}`}>
                      {l.kind === "photos_zip" ? <Images size={11} className="mr-1" /> : <FileSpreadsheet size={11} className="mr-1" />}
                      {KIND_LABEL[l.kind] || l.kind}
                    </Badge>
                    <span className="font-heading text-sm font-semibold text-[#1A2024]">{l.organization}</span>
                  </div>
                  <div className="mt-0.5 font-body text-xs text-[#5C6873]">
                    {l.filename || "—"} · by {l.imported_by} · {l.created_at ? new Date(l.created_at).toLocaleString() : ""}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex gap-4 font-body text-xs text-[#5C6873]">
                    <span><span className="font-semibold text-[#4A7C59]">{l.created}</span> created</span>
                    <span><span className="font-semibold text-[#2E3A46]">{l.photos}</span> photos</span>
                    <span><span className="font-semibold text-[#1A2024]">{l.skipped}</span> skipped</span>
                    <span><span className="font-semibold text-[#9E4747]">{l.errors_count}</span> errors</span>
                  </div>
                  {l.undone ? (
                    <Badge variant="outline" className="rounded-full border-[#9E4747]/30 text-[11px] text-[#9E4747]" data-testid={`undone-badge-${l.id}`}>
                      Undone
                    </Badge>
                  ) : l.undoable ? (
                    <Button
                      variant="outline" size="sm"
                      className="border-[#9E4747]/40 text-[#9E4747] hover:bg-[#9E4747]/5 hover:text-[#9E4747]"
                      disabled={undoing === l.id}
                      onClick={() => undoImport(l)}
                      data-testid={`undo-import-${l.id}`}
                    >
                      <Undo2 size={13} className="mr-1" /> {undoing === l.id ? "Undoing…" : "Undo"}
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <Dialog open={!!viewOrg} onOpenChange={(o) => !o && setViewOrg(null)}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-2xl" data-testid="org-cases-dialog">
          <DialogHeader>
            <DialogTitle>Cases from {viewOrg}</DialogTitle>
            <DialogDescription>
              {orgData ? `${orgData.total} case(s) — ${orgData.ante_mortem.length} missing, ${orgData.post_mortem.length} unidentified` : "Loading…"}
            </DialogDescription>
          </DialogHeader>
          {orgData && (
            <div className="space-y-2" data-testid="org-cases-list">
              {orgData.total === 0 && <p className="font-body text-sm text-[#5C6873]">No cases attributed to this organisation.</p>}
              {[...orgData.ante_mortem.map((c) => ({ ...c, _kind: "ante" })), ...orgData.post_mortem.map((c) => ({ ...c, _kind: "post" }))].map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 rounded-md border border-[#E2E8F0] bg-white p-3" data-testid={`org-case-${c.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`rounded-full text-[10px] ${c._kind === "ante" ? "border-[#3B6EA5]/30 text-[#3B6EA5]" : "border-[#4A7C59]/30 text-[#4A7C59]"}`}>
                        {c._kind === "ante" ? "Missing" : "Unidentified"}
                      </Badge>
                      <span className="font-heading text-sm font-semibold text-[#1A2024]">{c.reference_code || c.full_name || "—"}</span>
                    </div>
                    <div className="mt-0.5 font-body text-xs text-[#5C6873]">
                      {c.full_name ? `${c.full_name} · ` : ""}{c.country || "?"}{c.location ? ` · ${c.location}` : ""} · {c.gender !== "unknown" ? c.gender : "gender ?"} · age {c.age_min ?? "?"}-{c.age_max ?? "?"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
